'use client'
// Section Documentation réservée aux admins (lecture des .md via /api/docs).
// Extrait de app/page.tsx (Phase 3 — séparation client/admin).
// TODO: renderMarkdown est une regex naïve → passer à react-markdown côté serveur.
import { useEffect, useState } from 'react'
import { SectionLabel } from '@/components/landing/SectionLabel'

interface Doc {
  name: string; filename: string; path: string
  content: string; lines: number; words: number
}

function renderMarkdown(md: string): string {
  return md
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/```[\w]*\n([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    .replace(/^---$/gm, '<hr />')
    .replace(/\n\n/g, '</p><p>')
}

export function DocsSection() {
  const [docs, setDocs] = useState<Doc[]>([])
  const [selected, setSelected] = useState<Doc | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetch('/api/docs')
      .then(r => r.json())
      .then(data => { setDocs(data.documents ?? []); setLoading(false) })
      .catch(err => { setError(err.message); setLoading(false) })
  }, [])

  if (loading) return (
    <section style={{ marginBottom: '48px' }}>
      <SectionLabel>Documentation</SectionLabel>
      <p style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>Chargement des documents…</p>
    </section>
  )

  if (error) return (
    <section style={{ marginBottom: '48px' }}>
      <SectionLabel>Documentation</SectionLabel>
      <p style={{ color: 'var(--coral)', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>Erreur : {error}</p>
    </section>
  )

  return (
    <section style={{ marginBottom: '48px' }}>
      <SectionLabel>Documentation</SectionLabel>
      <div style={{ marginBottom: '24px' }}>
        <select
          value={selected?.name ?? ''}
          onChange={e => { const doc = docs.find(d => d.name === e.target.value); setSelected(doc ?? null) }}
          style={{
            background: 'var(--bg-panel)', border: '1px solid var(--border)',
            borderRadius: '8px', padding: '10px 12px', color: 'var(--white)',
            fontSize: '13px', fontFamily: 'var(--font-mono)', outline: 'none',
            width: '100%', cursor: 'pointer', transition: 'border-color 0.15s',
          }}
          onFocus={e => (e.target.style.borderColor = 'var(--accent)')}
          onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
        >
          <option value="">-- Choisir un document --</option>
          {docs.map(doc => (
            <option key={doc.name} value={doc.name}>{doc.name}.md ({doc.lines} lignes)</option>
          ))}
        </select>
      </div>
      {selected && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '12px', padding: '24px', animation: 'fadeUp 0.3s var(--ease-out)',
        }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: '16px' }}>
            ◈ /docs/{selected.filename}
          </div>
          <div
            className="md-content"
            dangerouslySetInnerHTML={{ __html: `<p>${renderMarkdown(selected.content)}</p>` }}
            style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.75, fontFamily: 'var(--font-mono)' }}
          />
          <style>{`
            .md-content h1 { font-size: 22px; font-weight: 700; color: var(--white); margin: 32px 0 12px; letter-spacing: -0.02em; font-family: var(--font-display, inherit); }
            .md-content h2 { font-size: 17px; font-weight: 600; color: var(--white); margin: 28px 0 10px; padding-bottom: 6px; border-bottom: 1px solid var(--border-dim); font-family: var(--font-display, inherit); }
            .md-content h3 { font-size: 14px; font-weight: 600; color: var(--text-secondary); margin: 20px 0 8px; font-family: var(--font-display, inherit); }
            .md-content p  { margin: 0 0 14px; }
            .md-content pre { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 8px; padding: 16px; overflow-x: auto; margin: 16px 0; }
            .md-content code { background: var(--bg-panel); border: 1px solid var(--border-dim); border-radius: 4px; padding: 2px 6px; font-size: 12px; color: var(--accent); }
            .md-content pre code { background: none; border: none; padding: 0; color: var(--text-secondary); }
            .md-content li { margin: 4px 0 4px 16px; list-style: disc; }
            .md-content a  { color: var(--accent); text-decoration: none; border-bottom: 1px solid transparent; transition: border-color 0.15s; }
            .md-content a:hover { border-color: var(--accent); }
            .md-content hr { border: none; border-top: 1px solid var(--border-dim); margin: 24px 0; }
            .md-content strong { color: var(--white); font-weight: 600; }
            .md-content em { color: var(--text-secondary); font-style: italic; }
          `}</style>
        </div>
      )}
    </section>
  )
}
